import React, { useState } from "react";
import { api } from "../../lib/api";
import { apiErrorCode } from "../../lib/apiErrors";
import { useT, useLanguage } from "../../lib/LanguageContext";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { financeErrorText, financeCodeText, RELOAD_CODES } from "./financeErrorText";
import { priceWithCycle } from "./customPriceText";
import { instantDay, utcDay } from "../../lib/calendarDay";

// What the answer of the removal says happened (D4 contract section 4); an answer without a
// known status says the plain "restored" sentence.
const REMOVED_TEXT = {
  restored: "t360f.removePrice.done",
  restored_demo: "t360f.removePrice.doneDemo",
  ended_locally: "t360f.removePrice.doneLocal",
};

/**
 * The practice's custom price (`/360.custom_price`, D4 contract section 8.1) for every reader: the
 * price it is billed per cycle, when Stripe bills it from, until when it holds, whether it was only
 * recorded (demo) and whether the expiry restore is failing. For an operator who may adjust prices
 * (`canRemove`) it is also the explicit way to take it off again: a confirmation quoting the price the
 * practice returns to (`base_price`), then `DELETE …/billing/adjust-price`.
 *
 * `onChanged()` runs after the removal and after a refusal that means the page is stale or shows a
 * Stripe call still unconfirmed (RELOAD_CODES), so the page re-reads itself.
 */
export default function CustomPriceBadge({ customPrice, canRemove, tenantId, onChanged }) {
  const t = useT("superadmin");
  const { lang } = useLanguage();
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  if (!customPrice) return null;

  const until = utcDay(customPrice.effective_until, lang);
  const billsFrom = instantDay(customPrice.bills_from, lang);
  const restoreError = customPrice.restore_error;

  const remove = async (e) => {
    e.preventDefault(); // the dialog closes when the answer is in, not when the button is pressed
    setBusy(true);
    try {
      const r = await api.delete(`/super-admin/tenants/${tenantId}/billing/adjust-price`);
      toast.success(t(REMOVED_TEXT[r.data?.status] || REMOVED_TEXT.restored));
      setConfirming(false);
      onChanged();
    } catch (err) {
      toast.error(financeErrorText(err, { t, fallback: t("t360.error") }));
      setConfirming(false);
      if (RELOAD_CODES.includes(apiErrorCode(err))) onChanged();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-3 ml-auto text-xs text-amber-600" data-testid="custom-price-badge">
      <span>
        ⚡ {t("t360f.customPrice.title")}
        <span className="ml-2 font-medium">{priceWithCycle(customPrice.price, customPrice.cycle, t)}</span>
        {until && <span className="ml-2">· {t("t360f.customPrice.until", { date: until })}</span>}
        {billsFrom && <span className="ml-2">· {t("t360f.customPrice.billsFrom", { date: billsFrom })}</span>}
        {customPrice.demo_mode && <span className="ml-2">· {t("t360f.customPrice.demo")}</span>}
      </span>
      {restoreError && (
        <p role="alert" data-testid="custom-price-restore-failing"
          className="basis-full text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-lg p-2">
          {t("t360f.customPrice.restoreFailing")} {financeCodeText(restoreError.code, t)}
        </p>
      )}
      {canRemove && (
        <Button size="sm" variant="outline" onClick={() => setConfirming(true)} data-testid="custom-price-remove"
          className="border-amber-200 text-amber-600 hover:bg-amber-100">
          {t("t360f.customPrice.remove")}
        </Button>
      )}
      <AlertDialog open={confirming} onOpenChange={(open) => { if (!open && !busy) setConfirming(false); }}>
        <AlertDialogContent data-testid="custom-price-remove-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("t360f.removePrice.title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("t360f.removePrice.confirm", { price: priceWithCycle(customPrice.base_price, customPrice.cycle, t) })}
              <span className="block mt-2" data-testid="custom-price-remove-owner-notified">{t("t360f.removePrice.ownerNotified")}</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy} data-testid="custom-price-remove-cancel">{t("t360f.removePrice.cancel")}</AlertDialogCancel>
            <AlertDialogAction disabled={busy} onClick={remove} data-testid="custom-price-remove-confirm">
              {t("t360f.removePrice.action")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
